import $ from 'jquery';

const CONFIG = {
    CLOSE: '.js-boxClose'
};

/**
 * @fileOverview View handles updating the visual elements on the page
 */
export default class BoxView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        this.init();
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        this.isEnabled = false;

        return this.setupHandlers()
            .createChildren()
            .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {BoxView}
     */
    setupHandlers() {
        this.handleClick = this.onClick.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {BoxView}
     */
    createChildren() {
        this.$close = this.$element.find(CONFIG.CLOSE);

        return this;
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {BoxView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].addEventListener('click', this.handleClick);
        }

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {BoxView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].removeEventListener('click', this.handleClick);
        }

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {BoxView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Handles click event
     * 
     * @param event
     * @returns {BoxView}
     */
    onClick(event) {
        const $target = $(event.currentTarget);
        let $next = $target.closest(this.$element).next();

        if (!$next.length) {
            $next = $target.closest(this.$element).parent().next();
        }

        $target.closest(this.$element).attr('aria-hidden', 'true').slideUp(250, () => {
            $target.blur();
            $target.closest(this.$element).remove();
            $next.find(':not(:has(*))').first().attr('tabindex', '-1').focus();
        });

        return this;
    }
}
